import TITLES from "constants/titles";
import type { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { formatPhoneNumber, getFullName } from "utils/common";

/**
 * Builds a map of merge tag names (as they appear after SUNO:) to replacement values
 * for use when sending preview emails with patient context.
 * Missing values are returned as "".
 */
export function getMergeTagValuesMap(
  patient: Patient | null | undefined,
  clinic: Clinic | null | undefined
): Record<string, string> {
  const practice = clinic?.practice;
  const preferredProvider = patient?.preferred_provider;

  const patientFullName = getFullName({
    firstName: patient?.first_name ?? "",
    middleName: patient?.middle_name ?? "",
    preferredName: patient?.preferred_name ?? "",
    lastName: patient?.last_name ?? ""
  });

  const clinicAddress = clinic
    ? [
        clinic.street_address_1,
        clinic.street_address_2,
        clinic.city,
        [clinic.state, clinic.zip_code].filter(Boolean).join(" ").trim(),
        clinic.country
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const doctorFullName =
    preferredProvider?.fullName ??
    (preferredProvider
      ? getFullName({
          firstName: preferredProvider.first_name ?? "",
          middleName: undefined,
          preferredName: undefined,
          lastName: preferredProvider.last_name ?? ""
        })
      : "");

  return {
    patient_first_name: patient?.first_name ?? "",
    patient_preferred_name: patient?.preferred_name ?? "",
    patient_full_name: patientFullName,
    practice_name: practice?.name ?? "",
    clinic_name: clinic?.name ?? "",
    clinic_display_name: clinic?.display_name ?? "",
    clinic_phone: formatPhoneNumber(clinic?.phone ?? "", "parenthesis") ?? "",
    clinic_instructions:
      (clinic as { instructions?: string })?.instructions ?? "",
    google_review_url:
      (clinic as { google_review_url?: string })?.google_review_url ?? "",
    google_maps_url:
      (clinic as { google_maps_url?: string })?.google_maps_url ?? "",
    clinic_address: clinicAddress,
    doctor_title: TITLES.getFromCode(preferredProvider?.title) ?? "",
    doctor_first_name: preferredProvider?.first_name ?? "",
    doctor_last_name: preferredProvider?.last_name ?? "",
    doctor_full_name: doctorFullName,
    reminder_confirmation_url: "",
    private_feedback_request_url: "",
    time: "",
    date: "",
    prev_date: "",
    prev_time: "",
    patient_portal_link: ""
  };
}

/**
 * Replaces SUNO merge tags in HTML with values from the provided map.
 * Handles both <!--SUNO:tag--> and !--SUNO:tag-- patterns.
 * Tags not in the map or with no value are replaced with "".
 */
export function replaceMergeTagsWithValues(
  html: string,
  valuesMap: Record<string, string>
): string {
  let result = html;

  // Replace <!--SUNO:tag_name--> pattern
  result = result.replaceAll(/<!--SUNO:([^>]+?)-->/g, (_, tagName: string) => {
    const key = tagName.trim();
    return escapeHtmlValue(valuesMap[key] ?? "");
  });

  // Replace !--SUNO:tag_name-- pattern (no angle brackets)
  result = result.replaceAll(/!--SUNO:([^-]+?)--/g, (_, tagName: string) => {
    const key = tagName.trim();
    return escapeHtmlValue(valuesMap[key] ?? "");
  });

  return result;
}

function escapeHtmlValue(value: string): string {
  if (!value) return "";
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
